import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Eye, Download, CheckCircle, FileCheck, X, Pencil, Trash2, Mail } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import i18n from "@/i18n";

const formatCurrency = (value) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol'
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const styles = {
    Aceptada: "bg-green-100 text-green-700",
    Pendiente: "bg-amber-100 text-amber-700",
    Rechazada: "bg-red-100 text-red-700"
  };
  const labels = {
    Aceptada: t('common.accepted'),
    Pendiente: t('common.pending'),
    Rechazada: t('common.rejected')
  };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded text-xs font-semibold ${styles[status] || styles.Pendiente}`}>
      {labels[status] || status}
    </span>
  );
};

export default function DocumentosSoporte() {
  const { t } = useTranslation();
  const [documentos, setDocumentos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [createdDocumento, setCreatedDocumento] = useState(null);
  const [emailData, setEmailData] = useState({ recipient_email: "", recipient_name: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  const [formData, setFormData] = useState({
    proveedor_nombre: "",
    proveedor_cedula: "",
    concepto: "",
    valor_total: "",
    fecha_transaccion: new Date().toISOString().split('T')[0],
    observaciones: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchDocumentos = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter && statusFilter !== "all") params.append("estado", statusFilter);
      if (dateFrom) params.append("fecha_inicio", dateFrom);
      if (dateTo) params.append("fecha_fin", dateTo);
      
      const response = await axios.get(`${API}/documentos-soporte?${params.toString()}`);
      setDocumentos(response.data);
    } catch (error) {
      toast.error(t('supportDocs.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProveedores = async () => {
    try {
      const response = await axios.get(`${API}/proveedores`);
      setProveedores(response.data);
    } catch (error) {
      console.error("Error fetching proveedores:", error);
    }
  };

  useEffect(() => {
    fetchDocumentos();
    fetchProveedores();
  }, [searchTerm, statusFilter, dateFrom, dateTo]);

  const validateForm = () => {
    const errors = {};
    if (!formData.proveedor_nombre.trim()) errors.proveedor_nombre = t('common.required');
    if (!formData.proveedor_cedula.trim()) errors.proveedor_cedula = t('common.required');
    if (!formData.concepto.trim()) errors.concepto = t('common.required');
    if (!formData.valor_total || formData.valor_total <= 0) errors.valor_total = t('common.mustBeGreaterThanZero');
    if (!formData.fecha_transaccion) errors.fecha_transaccion = t('common.required');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProveedorSelect = (proveedorId) => {
    const proveedor = proveedores.find(p => p.id === proveedorId);
    if (proveedor) {
      setFormData({
        ...formData,
        proveedor_nombre: proveedor.nombre,
        proveedor_cedula: proveedor.cedula
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/documentos-soporte`, {
        ...formData,
        valor_total: parseFloat(formData.valor_total)
      });
      
      setCreatedDocumento(response.data);
      setShowCreateModal(false);
      setShowSuccessModal(true);
      setFormData({
        proveedor_nombre: "",
        proveedor_cedula: "",
        concepto: "",
        valor_total: "",
        fecha_transaccion: new Date().toISOString().split('T')[0],
        observaciones: ""
      });
      fetchDocumentos();
    } catch (error) {
      toast.error(t('supportDocs.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async (documento) => {
    try {
      const response = await axios.get(`${API}/documentos-soporte/${documento.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DocSoporte_${documento.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('common.pdfDownloaded'));
    } catch (error) {
      toast.error(t('common.pdfDownloadError'));
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const handleEdit = (documento) => {
    setSelectedDocumento(documento);
    setFormData({
      proveedor_nombre: documento.proveedor_nombre,
      proveedor_cedula: documento.proveedor_cedula,
      concepto: documento.concepto,
      valor_total: documento.valor_total,
      fecha_transaccion: documento.fecha_transaccion,
      observaciones: documento.observaciones || "",
      estado: documento.estado
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/documentos-soporte/${selectedDocumento.id}`, {
        ...formData,
        valor_total: parseFloat(formData.valor_total)
      });
      
      toast.success(t('supportDocs.updateSuccess'));
      setShowEditModal(false);
      resetForm();
      fetchDocumentos();
    } catch (error) {
      toast.error(t('supportDocs.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/documentos-soporte/${selectedDocumento.id}`);
      toast.success(t('supportDocs.deleteSuccess', { number: selectedDocumento.numero }));
      setShowDeleteDialog(false);
      setSelectedDocumento(null);
      fetchDocumentos();
    } catch (error) {
      toast.error(t('supportDocs.deleteError'));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailData.recipient_email) {
      toast.error(t('common.enterValidEmail'));
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/documentos-soporte/${selectedDocumento.id}/send-email`, emailData);
      toast.success(t('supportDocs.sentSuccess', { email: emailData.recipient_email }));
      setShowEmailModal(false);
      setEmailData({ recipient_email: "", recipient_name: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('supportDocs.sendError'));
    } finally {
      setSendingEmail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      proveedor_nombre: "",
      proveedor_cedula: "",
      concepto: "",
      valor_total: "",
      fecha_transaccion: new Date().toISOString().split('T')[0],
      observaciones: ""
    });
    setFormErrors({});
  };

  return (
    <div data-testid="documentos-soporte-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow']">{t('supportDocs.title')}</h1>
          <p className="text-slate-500 mt-1">{t('supportDocs.subtitle')}</p>
        </div>
        <Button data-testid="btn-nuevo-documento" onClick={() => setShowCreateModal(true)} className="bg-[#0f2a4a] hover:bg-[#1e3a8a] text-white">
          <Plus className="w-4 h-4 mr-2" />{t('supportDocs.newDocument')}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input data-testid="search-input" placeholder={t('supportDocs.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-[160px]">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="Aceptada">{t('common.accepted')}</SelectItem>
                <SelectItem value="Pendiente">{t('common.pending')}</SelectItem>
                <SelectItem value="Rechazada">{t('common.rejected')}</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" />
            {(searchTerm || statusFilter || dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> {t('common.clear')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : documentos.length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">{t('supportDocs.noDocsFound')}</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>{t('supportDocs.createFirstDoc')}</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-600">{t('supportDocs.documentNumber')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('common.date')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('supportDocs.supplier')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('supportDocs.cedula')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('supportDocs.concept')}</TableHead>
                    <TableHead className="font-semibold text-slate-600 text-right">{t('supportDocs.value')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('common.status')}</TableHead>
                    <TableHead className="font-semibold text-slate-600 text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentos.map((documento) => (
                    <TableRow key={documento.id} data-testid={`documento-row-${documento.numero}`} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium text-slate-900">{documento.numero}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(documento.fecha)}</TableCell>
                      <TableCell className="text-slate-900">{documento.proveedor_nombre}</TableCell>
                      <TableCell className="text-slate-600">{documento.proveedor_cedula}</TableCell>
                      <TableCell className="text-slate-600 max-w-[200px] truncate">{documento.concepto}</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(documento.valor_total)}</TableCell>
                      <TableCell><StatusBadge status={documento.estado} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" data-testid={`btn-ver-${documento.numero}`} onClick={() => { setSelectedDocumento(documento); setShowViewModal(true); }} title={t('common.view')}><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-edit-${documento.numero}`} onClick={() => handleEdit(documento)} title={t('common.edit')}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-download-${documento.numero}`} onClick={() => handleDownloadPDF(documento)} title={t('common.downloadPdf')}><Download className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-email-${documento.numero}`} onClick={() => { setSelectedDocumento(documento); setEmailData({ recipient_email: "", recipient_name: documento.proveedor_nombre }); setShowEmailModal(true); }} title={t('common.sendByEmail')}><Mail className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-delete-${documento.numero}`} onClick={() => { setSelectedDocumento(documento); setShowDeleteDialog(true); }} className="text-red-600 hover:text-red-700 hover:bg-red-50" title={t('common.delete')}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('supportDocs.createTitle')}</DialogTitle>
            <DialogDescription>{t('supportDocs.createDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {proveedores.length > 0 && (
              <div>
                <Label>{t('supportDocs.selectSupplier')}</Label>
                <Select onValueChange={handleProveedorSelect}>
                  <SelectTrigger><SelectValue placeholder={t('supportDocs.selectSupplierPlaceholder')} /></SelectTrigger>
                  <SelectContent>
                    {proveedores.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre} - {p.cedula}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="proveedor_nombre">{t('supportDocs.supplierName')}</Label>
                <Input id="proveedor_nombre" data-testid="input-proveedor-nombre" value={formData.proveedor_nombre} onChange={(e) => setFormData({...formData, proveedor_nombre: e.target.value})} className={formErrors.proveedor_nombre ? "border-red-500" : ""} />
                {formErrors.proveedor_nombre && <p className="text-red-500 text-xs mt-1">{formErrors.proveedor_nombre}</p>}
              </div>
              <div>
                <Label htmlFor="proveedor_cedula">{t('supportDocs.supplierCedula')}</Label>
                <Input id="proveedor_cedula" data-testid="input-proveedor-cedula" value={formData.proveedor_cedula} onChange={(e) => setFormData({...formData, proveedor_cedula: e.target.value})} className={formErrors.proveedor_cedula ? "border-red-500" : ""} />
                {formErrors.proveedor_cedula && <p className="text-red-500 text-xs mt-1">{formErrors.proveedor_cedula}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="concepto">{t('supportDocs.purchaseConcept')}</Label>
              <Input id="concepto" data-testid="input-concepto" value={formData.concepto} onChange={(e) => setFormData({...formData, concepto: e.target.value})} className={formErrors.concepto ? "border-red-500" : ""} />
              {formErrors.concepto && <p className="text-red-500 text-xs mt-1">{formErrors.concepto}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valor_total">{t('supportDocs.totalValue')}</Label>
                <Input id="valor_total" type="number" min="0" step="1000" data-testid="input-valor-total" value={formData.valor_total} onChange={(e) => setFormData({...formData, valor_total: e.target.value})} className={formErrors.valor_total ? "border-red-500" : ""} />
                {formErrors.valor_total && <p className="text-red-500 text-xs mt-1">{formErrors.valor_total}</p>}
              </div>
              <div>
                <Label htmlFor="fecha_transaccion">{t('supportDocs.transactionDate')}</Label>
                <Input id="fecha_transaccion" type="date" data-testid="input-fecha-transaccion" value={formData.fecha_transaccion} onChange={(e) => setFormData({...formData, fecha_transaccion: e.target.value})} className={formErrors.fecha_transaccion ? "border-red-500" : ""} />
                {formErrors.fecha_transaccion && <p className="text-red-500 text-xs mt-1">{formErrors.fecha_transaccion}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="observaciones">{t('common.observationsOptional')}</Label>
              <Textarea id="observaciones" data-testid="input-observaciones" value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} rows={3} />
            </div>
            {formData.valor_total > 0 && (
              <div className="bg-[#0f2a4a] text-white p-4 rounded-lg text-center">
                <p className="text-sm opacity-80">{t('supportDocs.operationTotalValue')}</p>
                <p className="text-2xl font-bold">{formatCurrency(parseFloat(formData.valor_total) || 0)}</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>{t('common.cancel')}</Button>
              <Button type="submit" data-testid="btn-submit-documento" disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2">
                {submitting ? t('common.processing') : t('supportDocs.createButton')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Barlow']">{t('supportDocs.successTitle')}</h3>
            <p className="text-slate-600 mb-4">{t('supportDocs.successMessage', { number: createdDocumento?.numero })}</p>
            <div className="bg-slate-50 p-4 rounded-lg text-left mb-4">
              <p className="text-sm"><span className="text-slate-500">{t('supportDocs.successSupplier')}</span> {createdDocumento?.proveedor_nombre}</p>
              <p className="text-sm"><span className="text-slate-500">{t('supportDocs.successValue')}</span> {formatCurrency(createdDocumento?.valor_total || 0)}</p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowSuccessModal(false)}>{t('common.close')}</Button>
              <Button className="bg-[#0f2a4a] hover:bg-[#1e3a8a]" onClick={() => { if (createdDocumento) handleDownloadPDF(createdDocumento); }}>
                <Download className="w-4 h-4 mr-2" />{t('common.downloadPdf')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('supportDocs.previewTitle')} - {selectedDocumento?.numero}</DialogTitle>
            <DialogDescription>{t('supportDocs.previewDescription')}</DialogDescription>
          </DialogHeader>
          {selectedDocumento && (
            <div className="space-y-6">
              <div className="flex justify-between items-start pb-4 border-b">
                <div>
                  <h3 className="text-xl font-bold text-[#0f2a4a] font-['Barlow']">{t('company.name')}</h3>
                  <p className="text-sm text-slate-500">{t('company.taxIdLabel')}: {t('company.taxId')}</p>
                  <p className="text-sm text-slate-500">{t('company.address')}</p>
                  <p className="text-sm text-slate-500">{t('company.city')}</p>
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-bold text-[#0f2a4a] font-['Barlow']">{t('supportDocs.documentTitle')}</h2>
                  <p className="text-xs text-slate-500 mt-1">{t('supportDocs.documentSubtitle')}</p>
                  <p className="text-sm text-slate-600 mt-2">N°: {selectedDocumento.numero}</p>
                  <p className="text-sm text-slate-600">{t('common.date')}: {formatDate(selectedDocumento.fecha)}</p>
                  <StatusBadge status={selectedDocumento.estado} />
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <p className="text-sm text-amber-800 font-medium">{t('supportDocs.legalTitle')}</p>
                <p className="text-xs text-amber-700 mt-1">{t('supportDocs.legalText')}</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs text-slate-500 uppercase mb-2">{t('supportDocs.supplierNatural')}</p>
                <p className="font-semibold text-slate-900">{selectedDocumento.proveedor_nombre}</p>
                <p className="text-sm text-slate-600">{t('supportDocs.cedulaLabel')} {selectedDocumento.proveedor_cedula}</p>
              </div>
              <div className="space-y-3">
                <div className="flex gap-4">
                  <span className="text-slate-500 font-medium w-48">{t('supportDocs.purchaseConceptLabel')}</span>
                  <span className="text-slate-900">{selectedDocumento.concepto}</span>
                </div>
                <div className="flex gap-4">
                  <span className="text-slate-500 font-medium w-48">{t('supportDocs.transactionDateLabel')}</span>
                  <span className="text-slate-900">{formatDate(selectedDocumento.fecha_transaccion)}</span>
                </div>
              </div>
              <div className="bg-[#0f2a4a] text-white p-6 rounded-lg text-center">
                <p className="text-sm opacity-80">{t('supportDocs.totalOperationValue')}</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(selectedDocumento.valor_total)}</p>
              </div>
              {selectedDocumento.observaciones && (
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#0f2a4a]">
                  <p className="text-xs text-slate-500 uppercase mb-1">{t('common.observations')}</p>
                  <p className="text-sm text-slate-700">{selectedDocumento.observaciones}</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowViewModal(false)}>{t('common.close')}</Button>
                <Button className="bg-[#0f2a4a] hover:bg-[#1e3a8a]" onClick={() => handleDownloadPDF(selectedDocumento)}>
                  <Download className="w-4 h-4 mr-2" />{t('common.downloadPdf')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('supportDocs.editTitle')} {selectedDocumento?.numero}</DialogTitle>
            <DialogDescription>{t('supportDocs.editDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_proveedor_nombre">{t('supportDocs.supplierName')}</Label>
                <Input id="edit_proveedor_nombre" value={formData.proveedor_nombre} onChange={(e) => setFormData({...formData, proveedor_nombre: e.target.value})} className={formErrors.proveedor_nombre ? "border-red-500" : ""} />
              </div>
              <div>
                <Label htmlFor="edit_proveedor_cedula">{t('supportDocs.supplierCedula')}</Label>
                <Input id="edit_proveedor_cedula" value={formData.proveedor_cedula} onChange={(e) => setFormData({...formData, proveedor_cedula: e.target.value})} className={formErrors.proveedor_cedula ? "border-red-500" : ""} />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_concepto">{t('supportDocs.purchaseConcept')}</Label>
              <Input id="edit_concepto" value={formData.concepto} onChange={(e) => setFormData({...formData, concepto: e.target.value})} className={formErrors.concepto ? "border-red-500" : ""} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit_valor_total">{t('supportDocs.totalValue')}</Label>
                <Input id="edit_valor_total" type="number" min="0" step="1000" value={formData.valor_total} onChange={(e) => setFormData({...formData, valor_total: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="edit_fecha_transaccion">{t('supportDocs.transactionDateShort')}</Label>
                <Input id="edit_fecha_transaccion" type="date" value={formData.fecha_transaccion} onChange={(e) => setFormData({...formData, fecha_transaccion: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="edit_estado">{t('common.status')}</Label>
                <Select value={formData.estado} onValueChange={(v) => setFormData({...formData, estado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aceptada">{t('common.accepted')}</SelectItem>
                    <SelectItem value="Pendiente">{t('common.pending')}</SelectItem>
                    <SelectItem value="Rechazada">{t('common.rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="edit_observaciones">{t('common.observations')}</Label>
              <Textarea id="edit_observaciones" value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} rows={2} />
            </div>
            {formData.valor_total > 0 && (
              <div className="bg-[#0f2a4a] text-white p-4 rounded-lg text-center">
                <p className="text-sm opacity-80">{t('supportDocs.totalValueShort')}</p>
                <p className="text-2xl font-bold">{formatCurrency(parseFloat(formData.valor_total) || 0)}</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2">
                {submitting ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('supportDocs.deleteTitle', { number: selectedDocumento?.numero })}</AlertDialogTitle>
            <AlertDialogDescription>{t('supportDocs.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('supportDocs.emailTitle')}</DialogTitle>
            <DialogDescription>{t('supportDocs.emailDescription', { number: selectedDocumento?.numero })}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <Label htmlFor="doc_recipient_email">{t('common.recipientEmail')}</Label>
              <Input id="doc_recipient_email" type="email" placeholder={t('common.emailPlaceholder')} value={emailData.recipient_email} onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})} required />
            </div>
            <div>
              <Label htmlFor="doc_recipient_name">{t('common.recipientName')}</Label>
              <Input id="doc_recipient_name" placeholder={t('common.nameOptional')} value={emailData.recipient_name} onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})} />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg text-sm">
              <p className="text-slate-600">{t('supportDocs.emailWillSend', { number: selectedDocumento?.numero })}</p>
              <ul className="mt-2 text-slate-500 list-disc list-inside">
                <li>{t('supportDocs.emailAttachment')}</li>
                <li>{t('supportDocs.emailSummary')}</li>
                <li>{t('supportDocs.emailContact')}</li>
              </ul>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEmailModal(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={sendingEmail} className="bg-[#0f2a4a] hover:bg-[#1e3a8a]">
                {sendingEmail ? t('common.sending') : <><Mail className="w-4 h-4 mr-2" />{t('common.sendEmail')}</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
